import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import A11yDialog, { DialogErrorLine } from '../../components/ui/A11yDialog';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 30px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
`;

const Row = styled.div`
  margin-bottom: 16px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const CompetencyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CompetencyCard = styled.div`
  background: #FAFBFC;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #E8ECF0;
`;

const CompetencyHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CompetencyIndex = styled.span`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.dark.little};
  padding: 4px;
  display: inline-flex;
  align-items: center;
  &:hover { color: ${({ theme }) => theme.colors.auxiliar.danger}; }
`;

const ScaleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 6px;
`;

// GERİYE DÖNÜK AĞIRLIK UYARISI. Ağırlık kuralı (birim = yüzde, dip toplam 100)
// sonradan eklendi ve var olan satırlar TARANMADI. Bu şerit, kuraldan önce
// yazılmış bir şablon açıldığında KAYITLI toplamı söyler ve formun ekranda ne
// yaptığını açıklar. Hata DEĞİLDİR (kırmızı değil, bilgilendirme) — kullanıcı
// kaydetmeden çıkarsa veri olduğu gibi kalır.
const LegacyNotice = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #FFF7E6;
  border: 1px solid #F0D9A8;
  color: #8A5A00;
  font-size: 12px;
  line-height: 1.5;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 14px;
  background: #fafbfc;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
`;

const TotalWeight = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $balanced }) => ($balanced ? '#1B7A3E' : '#B02A37')};
`;

const EqualBtn = styled.button`
  background: none;
  border: 1px solid #c7d0d9;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const AddBtn = styled.button`
  margin-top: 12px;
  background: none;
  border: 1.5px dashed #C7D0D9;
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; background: #F4FAFF; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const safeParseArray = (s) => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; }
};

const newCompetency = (key) => ({ key, label: '', scaleMin: 1, scaleMax: 5, weight: '', descriptor: '' });

// Coerce to a finite number, preserving a deliberate 0 but falling back to the
// default for empty/blank/invalid input. Türkçe ondalık virgülü kabul edilir
// (0,5 → 0.5) — virgül yüzünden değerin sessizce default'a düşmesi, "ağırlıklar
// kaydettikten sonra değişiyor" müşteri bulgusunun kök nedeniydi.
const toNum = (v, d) => {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return d;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : d;
};

// Ağırlık birimi YÜZDE: geçersiz/boş girişte NaN döner ki sessiz değişim yerine
// doğrulama hatası verilebilsin.
const parseWeight = (v) => {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return NaN;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

// Girilen ağırlıkları %100'e oranlar (2 hane); yuvarlama artığı son kaleme
// eklenir ki dip toplam tam 100 olsun.
const rescaleTo100 = (weights) => {
  const sum = weights.reduce((a, w) => a + w, 0);
  if (sum <= 0) {
    const eq = Math.round((100 / weights.length) * 100) / 100;
    const out = weights.map(() => eq);
    out[out.length - 1] = Math.round((100 - eq * (weights.length - 1)) * 100) / 100;
    return out;
  }
  const out = weights.map((w) => Math.round((w / sum) * 10000) / 100);
  const drift = Math.round((100 - out.reduce((a, w) => a + w, 0)) * 100) / 100;
  out[out.length - 1] = Math.round((out[out.length - 1] + drift) * 100) / 100;
  return out;
};

export default function ScorecardTemplateDialog({ open, template, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!template?.id;

  const [name, setName] = useState('');
  const [competencies, setCompetencies] = useState([]);
  const [panelDebrief, setPanelDebrief] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // FE-25 — "kirli form" kapısı: yetkinlik listesi düzenlenmişse ESC atma onayı sorar.
  const [touched, setTouched] = useState(false);
  // FE-40 — istemci doğrulaması artık toast değil, diyalog içinde role="alert".
  const [formError, setFormError] = useState(null);
  // Kural öncesi yazılmış şablonun KAYITLI ağırlık toplamı (null: sorun yok).
  // Rakam saklanır, metin değil: uyarı i18n'den kurulur.
  const [legacyWeightSum, setLegacyWeightSum] = useState(null);
  // Preserve scope that may have been set via the API; the UI creates
  // tenant-default templates but must not wipe an existing pin on edit.
  const scopeRef = useRef({ requisitionId: null, jobPostingId: null });
  const keyRef = useRef(1);

  const genKey = () => `k${keyRef.current++}`;

  useEffect(() => {
    if (!open) return;
    setTouched(false);
    setFormError(null);
    setLegacyWeightSum(null);
    if (isEdit) {
      const parsed = safeParseArray(template.competenciesJson);
      // Seed the key counter past any numeric keys already present.
      const maxNum = parsed.reduce((m, c) => {
        const match = /^k(\d+)$/.exec(c?.key || '');
        return match ? Math.max(m, Number(match[1])) : m;
      }, 0);
      keyRef.current = maxNum + 1;
      // Eski şablonlar göreli ağırlıkla kaydedilmiş olabilir (örn. hepsi 1);
      // form yüzde birimiyle çalışır — açılışta %100'e oranlayarak göster.
      const rawWeights = parsed.map((c) => toNum(c?.weight, 0));
      // SESSİZ DÖNÜŞÜM ARTIK SÖYLENİYOR. Form ağırlıkları açılışta %100'e
      // oranlıyor; bu, kural öncesi şablonu düzenlenebilir kılar AMA kullanıcı
      // ekranda 80/10/10 görüp veritabanında 0.8/0.1/0.1 durduğunu bilmezse
      // "ağırlıklar kendiliğinden değişti" hissi doğuyordu. Kayıtlı toplam
      // %100 değilse şerit çıkar ve neyin neden değiştiğini söyler.
      const rawSum = Math.round(rawWeights.reduce((a, w) => a + w, 0) * 100) / 100;
      if (parsed.length > 0 && Math.abs(rawSum - 100) > 0.05) setLegacyWeightSum(rawSum);
      const pctWeights = rescaleTo100(rawWeights.map((w) => (w > 0 ? w : 0)));
      setCompetencies(parsed.map((c, i) => ({
        key: c?.key || genKey(),
        label: c?.label || '',
        scaleMin: c?.scaleMin ?? 1,
        scaleMax: c?.scaleMax ?? 5,
        weight: pctWeights[i],
        descriptor: c?.descriptor || '',
      })));
      setName(template.name || '');
      setPanelDebrief(!!template.isPanelDebriefRequired);
      scopeRef.current = {
        requisitionId: template.requisitionId ?? null,
        jobPostingId: template.jobPostingId ?? null,
      };
    } else {
      keyRef.current = 1;
      setName('');
      setCompetencies([{ ...newCompetency(genKey()), weight: 100 }]);
      setPanelDebrief(false);
      scopeRef.current = { requisitionId: null, jobPostingId: null };
    }
  }, [open, template]); // eslint-disable-line react-hooks/exhaustive-deps

  // Her kullanıcı düzenlemesi formu KİRLİ yapar (ESC'te atma onayı sorulur) ve
  // varsa açık doğrulama hatasını temizler.
  const markDirty = () => { setTouched(true); setFormError(null); };
  const updateCompetency = (key, field, value) => {
    markDirty();
    setCompetencies((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
  };
  const addCompetency = () => { markDirty(); setCompetencies((prev) => [...prev, newCompetency(genKey())]); };
  const removeCompetency = (key) => { markDirty(); setCompetencies((prev) => prev.filter((c) => c.key !== key)); };
  const distributeEqually = () => { markDirty(); return setCompetencies((prev) => {
    if (prev.length === 0) return prev;
    const weights = rescaleTo100(prev.map(() => 1));
    return prev.map((c, i) => ({ ...c, weight: weights[i] }));
  }); };

  // Canlı dip toplam — birim sorusunun cevabı ekranda: % üzerinden, hedef 100.
  const liveTotal = Math.round(
    competencies.reduce((a, c) => {
      const w = parseWeight(c.weight);
      return a + (Number.isFinite(w) ? w : 0);
    }, 0) * 100,
  ) / 100;
  const balanced = Math.abs(liveTotal - 100) <= 0.05;

  const handleSubmit = async () => {
    setFormError(null);
    const trimmedName = name.trim();
    if (!trimmedName) { setFormError(t('ats.scorecard.form.nameRequired')); return; }

    const cleaned = competencies
      .map((c) => ({
        key: c.key,
        label: (c.label || '').trim(),
        weight: parseWeight(c.weight),
        scaleMin: toNum(c.scaleMin, 1),
        scaleMax: toNum(c.scaleMax, 5),
        descriptor: (c.descriptor || '').trim(),
      }))
      .filter((c) => c.label);

    if (cleaned.length === 0) { setFormError(t('ats.scorecard.form.atLeastOneCompetency')); return; }

    if (cleaned.some((c) => c.scaleMax < c.scaleMin)) {
      setFormError(t('ats.scorecard.form.scaleError'));
      return;
    }

    // Birim yüzdedir: her ağırlık > 0 ve dip toplam 100 olmalı. Girilen değer
    // asla sessizce değiştirilmez — kural sağlanmıyorsa kayıt reddedilir.
    if (cleaned.some((c) => !Number.isFinite(c.weight) || c.weight <= 0)) {
      setFormError(t('ats.scorecard.form.invalidWeight'));
      return;
    }
    const totalWeight = Math.round(cleaned.reduce((a, c) => a + c.weight, 0) * 100) / 100;
    if (Math.abs(totalWeight - 100) > 0.05) {
      // Hata NEDENİ söyler: mevcut toplam kaç, hedef kaç ve çıkış yolu ne.
      // Eski metin yalnız kuralı tekrar ediyordu ("toplam %100 olmalıdır") —
      // kural öncesi bir şablonu düzenleyen kullanıcı neyin yanlış olduğunu ve
      // "Eşit Dağıt"ın bunu tek tıkla çözdüğünü göremiyordu.
      setFormError(t('ats.scorecard.form.weightsMustSum100Detailed', { total: totalWeight }));
      return;
    }

    const payload = {
      requisitionId: scopeRef.current.requisitionId,
      jobPostingId: scopeRef.current.jobPostingId,
      name: trimmedName,
      competenciesJson: JSON.stringify(cleaned),
      isPanelDebriefRequired: panelDebrief,
    };

    setSubmitting(true);
    try {
      const res = isEdit
        ? await api.put(`/v1/ats-scorecards/${template.id}`, payload)
        : await api.post('/v1/ats-scorecards', payload);
      if (res.data?.status) {
        onSaved?.();
        onClose?.();
      }
    } catch {
      // API errors are surfaced by the axios interceptor.
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <A11yDialog
      open={open}
      onClose={onClose}
      dirty={touched}
      overlay={Overlay}
      card={Card}
      overlayProps={{ $open: true }}
    >
      {({ titleId }) => (
      <>
        <Title id={titleId}>{isEdit ? t('ats.scorecard.form.titleEdit') : t('ats.scorecard.form.titleCreate')}</Title>
        <Subtitle>{t('ats.scorecard.form.subtitle')}</Subtitle>

        <Row>
          <TextInput
            label={t('ats.scorecard.form.nameLabel')}
            value={name}
            onChange={(e) => { markDirty(); setName(e.target.value); }}
            placeholder={t('ats.scorecard.form.namePlaceholder')}
            required
          />
        </Row>

        <Row>
          <SectionLabel>{t('ats.scorecard.competencies')}</SectionLabel>
          <CompetencyList>
            {competencies.map((c, idx) => (
              <CompetencyCard key={c.key}>
                <CompetencyHead>
                  <CompetencyIndex>{t('ats.scorecard.form.competencyIndex', { n: idx + 1 })}</CompetencyIndex>
                  <RemoveBtn
                    type="button"
                    onClick={() => removeCompetency(c.key)}
                    aria-label={t('ats.scorecard.form.removeCompetency')}
                  >
                    <Trash2 size={16} />
                  </RemoveBtn>
                </CompetencyHead>
                <TextInput
                  label={t('ats.scorecard.form.competencyName')}
                  value={c.label}
                  onChange={(e) => updateCompetency(c.key, 'label', e.target.value)}
                  placeholder={t('ats.scorecard.form.competencyNamePlaceholder')}
                />
                <ScaleGrid>
                  <TextInput
                    label={t('ats.scorecard.form.scaleMin')}
                    type="number"
                    min="0"
                    value={c.scaleMin}
                    onChange={(e) => updateCompetency(c.key, 'scaleMin', e.target.value)}
                  />
                  <TextInput
                    label={t('ats.scorecard.form.scaleMax')}
                    type="number"
                    min="0"
                    value={c.scaleMax}
                    onChange={(e) => updateCompetency(c.key, 'scaleMax', e.target.value)}
                  />
                  <TextInput
                    label={t('ats.scorecard.form.weight')}
                    type="text"
                    inputMode="decimal"
                    value={c.weight}
                    onChange={(e) => updateCompetency(c.key, 'weight', e.target.value)}
                  />
                </ScaleGrid>
                <div style={{ marginTop: 10 }}>
                  <TextInput
                    label={t('ats.scorecard.form.descriptor')}
                    value={c.descriptor}
                    onChange={(e) => updateCompetency(c.key, 'descriptor', e.target.value)}
                    placeholder={t('ats.scorecard.form.descriptorPlaceholder')}
                  />
                </div>
              </CompetencyCard>
            ))}
          </CompetencyList>
          <AddBtn type="button" onClick={addCompetency}>
            <Plus size={15} />{t('ats.scorecard.form.addCompetency')}
          </AddBtn>
          <TotalRow>
            <TotalWeight $balanced={balanced}>
              {t('ats.scorecard.form.totalWeight')}: {liveTotal}% / 100%
            </TotalWeight>
            <EqualBtn type="button" onClick={distributeEqually}>
              {t('ats.scorecard.form.distributeEqually')}
            </EqualBtn>
          </TotalRow>
          <Hint>{t('ats.scorecard.form.weightHint')}</Hint>
          {legacyWeightSum !== null && (
            <LegacyNotice data-testid="scorecard-legacy-weight-notice">
              {t('ats.scorecard.form.legacyWeightsNotice', { sum: legacyWeightSum })}
            </LegacyNotice>
          )}
        </Row>

        <Row>
          <CheckboxInput
            label={t('ats.scorecard.panelDebrief')}
            checked={panelDebrief}
            onChange={(v) => { markDirty(); setPanelDebrief(v); }}
          />
        </Row>

        {formError && <DialogErrorLine role="alert" data-testid="scorecard-template-form-error">{formError}</DialogErrorLine>}

        <Actions>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {isEdit ? t('common.save') : t('ats.scorecard.form.create')}
          </Button>
        </Actions>
      </>
      )}
    </A11yDialog>
  );
}
